import { X } from "lucide-react";
import { Dialog as DialogPrimitive, DropdownMenu as MenuPrimitive, Popover as PopoverPrimitive, Tooltip as TooltipPrimitive } from "radix-ui";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

// ------------------------------------------------------------------ Dialog
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/** A click on a toast raised while a modal is open is not an "outside" click: the modal stays open (QA F4). */
export function keepOpenOnToast(e: { target: EventTarget | null; preventDefault: () => void }) {
  const el = e.target as Element | null;
  if (el && typeof el.closest === "function" && el.closest("[data-sonner-toaster]")) e.preventDefault();
}

export function DialogContent({
  title,
  description,
  children,
  footer,
  size = "md",
  className,
  hideClose,
  onOpenAutoFocus,
}: {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  hideClose?: boolean;
  onOpenAutoFocus?: (e: Event) => void;
}) {
  const { t } = useT();
  const w = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" }[size];
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[var(--overlay)] backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
      <DialogPrimitive.Content
        onOpenAutoFocus={onOpenAutoFocus}
        onInteractOutside={keepOpenOnToast}
        className={cn(
          "fixed start-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-y-1/2 flex-col rounded-3xl border border-line bg-surface shadow-pop outline-none ltr:-translate-x-1/2 rtl:translate-x-1/2 data-[state=open]:animate-rise",
          w,
          className,
        )}
      >
        <header className="flex items-start gap-3 px-6 pt-5 pb-3">
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className="text-[17px] font-semibold text-ink">{title}</DialogPrimitive.Title>
            {description ? <DialogPrimitive.Description className="mt-1 text-[13px] text-ink-3">{description}</DialogPrimitive.Description> : <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>}
          </div>
          {!hideClose && (
            <DialogPrimitive.Close className="grid size-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-ink-2 hover:text-ink" aria-label={t("common.actions.close")}>
              <X className="size-4" />
            </DialogPrimitive.Close>
          )}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">{children}</div>
        {footer && <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-6 py-3.5">{footer}</footer>}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

// ------------------------------------------------------------------ Sheet (slides from the inline end: from the left in Arabic)
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = "end",
  width = "md",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: "end" | "start" | "bottom";
  width?: "sm" | "md" | "lg";
}) {
  const { t } = useT();
  const w = { sm: "sm:max-w-sm", md: "sm:max-w-lg", lg: "sm:max-w-2xl" }[width];
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[var(--overlay)] backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content
          onInteractOutside={keepOpenOnToast}
          className={cn(
            "fixed z-50 flex flex-col bg-surface shadow-pop outline-none",
            side === "bottom" ? "inset-x-0 bottom-0 max-h-[88dvh] rounded-t-3xl border-t border-line data-[state=open]:animate-[sheet-up_.32s_cubic-bezier(.22,1,.36,1)]" : cn("inset-y-0 w-full border-line", w),
            side === "end" && "end-0 border-s data-[state=open]:animate-[sheet-end_.32s_cubic-bezier(.22,1,.36,1)]",
            side === "start" && "start-0 border-e data-[state=open]:animate-[sheet-start_.32s_cubic-bezier(.22,1,.36,1)]",
          )}
        >
          <header className="flex items-start gap-3 border-b border-line px-5 pt-5 pb-4">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-[17px] font-semibold text-ink">{title}</DialogPrimitive.Title>
              {description ? <DialogPrimitive.Description className="mt-1 text-[13px] text-ink-3">{description}</DialogPrimitive.Description> : <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>}
            </div>
            <DialogPrimitive.Close className="grid size-8 shrink-0 place-items-center rounded-lg bg-surface-2 text-ink-2 hover:text-ink" aria-label={t("common.actions.close")}>
              <X className="size-4" />
            </DialogPrimitive.Close>
          </header>
          {/* focusable so keyboard users can scroll a sheet that has no controls (axe: scrollable-region-focusable) */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4" tabIndex={0}>
            {children}
          </div>
          {footer && <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3.5">{footer}</footer>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ------------------------------------------------------------------ Dropdown menu
export const Menu = MenuPrimitive.Root;
export const MenuTrigger = MenuPrimitive.Trigger;
export const MenuGroup = MenuPrimitive.Group;
export const MenuSub = MenuPrimitive.Sub;
export const MenuRadioGroup = MenuPrimitive.RadioGroup;

export const MenuContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof MenuPrimitive.Content>>(function MenuContent({ className, sideOffset = 6, align = "end", ...props }, ref) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content ref={ref} sideOffset={sideOffset} align={align} className={cn("z-[60] min-w-[200px] overflow-hidden rounded-2xl border border-line bg-surface p-1.5 shadow-pop data-[state=open]:animate-fade-in", className)} {...props} />
    </MenuPrimitive.Portal>
  );
});

const itemCls = "relative flex cursor-pointer select-none items-center gap-2.5 rounded-xl px-2.5 py-2 text-[13.5px] text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-surface-2 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-ink-3";

export const MenuItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof MenuPrimitive.Item> & { icon?: ReactNode; shortcut?: ReactNode; danger?: boolean }>(function MenuItem(
  { className, icon, shortcut, danger, children, ...props },
  ref,
) {
  return (
    <MenuPrimitive.Item ref={ref} className={cn(itemCls, danger && "text-bad [&_svg]:text-bad", className)} {...props}>
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {shortcut && <span className="ms-auto text-[11px] text-ink-3">{shortcut}</span>}
    </MenuPrimitive.Item>
  );
});

export function MenuRadioItem({ value, children, icon }: { value: string; children: ReactNode; icon?: ReactNode }) {
  return (
    <MenuPrimitive.RadioItem value={value} className={cn(itemCls, "pe-8")}>
      {icon}
      <span className="flex-1">{children}</span>
      <MenuPrimitive.ItemIndicator className="absolute end-2.5">
        <span className="block size-2 rounded-full bg-accent" />
      </MenuPrimitive.ItemIndicator>
    </MenuPrimitive.RadioItem>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <MenuPrimitive.Label className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-3">{children}</MenuPrimitive.Label>;
}
export function MenuSeparator() {
  return <MenuPrimitive.Separator className="my-1 h-px bg-line" />;
}
export function MenuSubTrigger({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <MenuPrimitive.SubTrigger className={itemCls}>
      {icon}
      <span className="flex-1">{children}</span>
    </MenuPrimitive.SubTrigger>
  );
}
export function MenuSubContent({ children }: { children: ReactNode }) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.SubContent sideOffset={6} className="z-[61] min-w-[180px] rounded-2xl border border-line bg-surface p-1.5 shadow-pop">
        {children}
      </MenuPrimitive.SubContent>
    </MenuPrimitive.Portal>
  );
}

// ------------------------------------------------------------------ Popover
export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverClose = PopoverPrimitive.Close;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>>(function PopoverContent({ className, sideOffset = 8, align = "end", ...props }, ref) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content ref={ref} sideOffset={sideOffset} align={align} className={cn("z-[60] w-80 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-line bg-surface p-3 shadow-pop outline-none data-[state=open]:animate-fade-in", className)} {...props} />
    </PopoverPrimitive.Portal>
  );
});

// ------------------------------------------------------------------ Tooltip
export const TooltipProvider = TooltipPrimitive.Provider;
export function Tooltip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "bottom" | "left" | "right" }) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} className="z-[70] max-w-xs rounded-lg bg-[#14121f] px-2.5 py-1.5 text-[12px] leading-snug text-white shadow-pop data-[state=delayed-open]:animate-fade-in dark:bg-[#ece9f6] dark:text-[#14121f]">
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
